/**
 * Transport-aware wrapper for the catalog READ (`CatalogApi.list`).
 *
 * Wraps the REST `CatalogApi` so `list()` takes the fast GraphQL path when the
 * central transport registry says so (`resolveTransport("catalog.list", …)`),
 * and TRANSPARENTLY falls back to REST on any GraphQL error — so flipping the
 * flag on can never harden into an outage. Everything else on the CatalogApi
 * (create/update/remove, assignments, primaries, logos) is delegated untouched:
 * writes and un-modeled reads stay on REST.
 *
 * GraphQL is used only when ALL hold, else REST:
 *   - a GraphQL client is configured,
 *   - the registry resolves the operation to "graphql",
 *   - the kind is one GraphQL serves (entity/tag/label/group — not address),
 *   - the query uses no filter GraphQL doesn't model yet (see
 *     {@link usesUnmodeledFilter}).
 */
import type { CatalogApi, CatalogListPage, CatalogListQuery } from "@authe/catalog";
import type { CatalogItemKind } from "@authe/fe-commons/catalog";
import type { FeatureFlags } from "@authe/fe-commons/flags";
import type { GraphQLClient } from "@authe/fe-commons/graphql";
import { catalogKindToGraphql, fetchCatalogItems, GraphQLRequestError } from "@authe/fe-commons/graphql";
import { resolveTransport } from "@authe/fe-commons/transport";
import { createLogger } from "@authe/fe-commons/logging";

const log = createLogger("dashboard:graphql-catalog");

export interface TransportAwareCatalogOptions {
  /** The GraphQL read client, or `null` when unconfigured (always REST). */
  graphqlClient: GraphQLClient | null;
  /** The live feature-flag set (registry precedence: master + per-op). */
  flags: FeatureFlags;
}

/**
 * Filters the GraphQL `catalogItems` read does not model — force REST when set.
 *
 * GraphQL serves the merged group list itself (curated rows plus the grouping
 * engine's cross-chain sagas), so both transports agree on WHAT exists. Asking
 * for one half of it is narrowing REST already implements, per half and before
 * the two are concatenated; routing that here is cheaper than a second
 * implementation behind two more schema arguments, and leaving it out entirely
 * is what makes a dropdown that changes nothing.
 */
function usesUnmodeledFilter(query: CatalogListQuery): boolean {
  // `cursor` is no longer on this list: GraphQL pages the same lanes as REST,
  // so falling back mid-walk would hand a cursor to the transport that did not
  // issue it. The two are still not interchangeable PER PAGE — a walk stays on
  // whichever transport started it, which is what the cursor being opaque means.
  return Boolean(query.chain || query.lifecycle || query.groupType || query.sagaKind);
}

export function withGraphqlCatalogList(
  rest: CatalogApi,
  { graphqlClient, flags }: TransportAwareCatalogOptions,
): CatalogApi {
  return {
    ...rest,
    async list(kind: CatalogItemKind, query: CatalogListQuery = {}): Promise<CatalogListPage> {
      const graphqlServesKind = catalogKindToGraphql(kind) != null;
      const wantsGraphql =
        graphqlClient != null &&
        graphqlServesKind &&
        !usesUnmodeledFilter(query) &&
        resolveTransport("catalog.list", flags, { graphqlConfigured: true }) === "graphql";

      if (wantsGraphql && graphqlClient) {
        try {
          return await fetchCatalogItems(graphqlClient, kind, {
            search: query.search,
            limit: query.limit,
            cursor: query.cursor,
          });
        } catch (err) {
          // Transport/GraphQL failure → fall back to REST. Anything else (a
          // programming error) surfaces so it isn't silently masked.
          if (!(err instanceof GraphQLRequestError)) throw err;
          log.warn("catalog.list GraphQL failed, falling back to REST", {
            kind,
            status: err.status,
            message: err.message,
          });
        }
      }
      return rest.list(kind, query);
    },
  };
}
